/** Inventory item create/edit and reorder-policy dialogs (used from the Spare parts pages). */
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  COSTING_METHODS,
  ITEM_KINDS,
  type Item,
  type ItemCategory,
  type ItemIn,
  type ItemPatch,
  type ReorderPolicy,
  useCreateItemCategory,
  useItemCategories,
  useSaveItem,
  useSetReorderPolicy,
} from "@/features/admin/reference/queries";
import {
  AttributeFields,
  type AttributeValues,
  compactAttributes,
  useAttributeDefinitions,
  withDefaults,
} from "@/shared/components/attribute-fields";
import { FormDialog } from "@/shared/components/form-dialog";
import { LocationPicker } from "@/shared/components/pickers";
import { nullable, numberOrNull, useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select, Textarea } from "@/shared/ui/form";

interface ItemValues extends Record<string, unknown> {
  sku: string;
  name: string;
  description: string;
  category_id: string;
  kind: string;
  uom: string;
  standard_cost: string;
  costing: string;
  barcode: string;
  manufacturer_part_no: string;
  tags: string;
  is_serialised: boolean;
  is_lot_tracked: boolean;
  is_returnable: boolean;
  is_active: boolean;
}

/** Inline category creator used inside the item form. */
function CategoryField({
  value,
  onChange,
  categories,
}: {
  value: string;
  onChange: (id: string) => void;
  categories: ItemCategory[];
}): ReactElement {
  const { t } = useTranslation();
  const create = useCreateItemCategory();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const add = async (): Promise<void> => {
    const key = name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    if (key.length < 2) {
      return;
    }
    const created = await create.mutateAsync({
      key,
      name: name.trim(),
      name_i18n: {},
      parent_id: null,
    });
    onChange(created.id);
    setName("");
    setAdding(false);
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Select id="item-category_id" value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Button type="button" size="sm" variant="outline" onClick={() => setAdding(!adding)}>
          {t("app.add")}
        </Button>
      </div>
      {adding ? (
        <div className="flex gap-2">
          <Input
            aria-label={t("inventory.newCategory")}
            placeholder={t("inventory.newCategory")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button
            type="button"
            size="sm"
            onClick={() => void add()}
            disabled={create.isPending || name.trim().length < 2}
          >
            {t("app.create")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Item create/edit dialog. */
export function ItemDialog({
  item,
  onClose,
  onCreated,
}: {
  item?: Item;
  onClose: () => void;
  onCreated?: (item: Item) => void;
}): ReactElement {
  const { t } = useTranslation();
  const save = useSaveItem();
  const categories = useItemCategories();
  const defs = useAttributeDefinitions("item");
  const [attributes, setAttributes] = useState<AttributeValues>(() =>
    withDefaults(defs.data ?? [], item?.attributes),
  );
  const initial = useMemo<ItemValues>(
    () => ({
      sku: item?.sku ?? "",
      name: item?.name ?? "",
      description: item?.description ?? "",
      category_id: item?.category_id ?? "",
      kind: item?.kind ?? "part",
      uom: item?.uom ?? "ea",
      standard_cost: item?.standard_cost ?? "",
      costing: item?.costing ?? "average",
      barcode: item?.barcode ?? "",
      manufacturer_part_no: item?.manufacturer_part_no ?? "",
      tags: item?.tags.join(", ") ?? "",
      is_serialised: item?.is_serialised ?? false,
      is_lot_tracked: item?.is_lot_tracked ?? false,
      is_returnable: item?.is_returnable ?? false,
      is_active: item?.is_active ?? true,
    }),
    [item],
  );
  const form = useForm<ItemValues>({
    initial,
    schema: z.object({ name: z.string().trim().min(1, t("app.required")) }),
    idPrefix: "item",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      const body: ItemIn | ItemPatch = {
        ...(item ? {} : { sku: nullable(v.sku) }),
        name: v.name.trim(),
        description: v.description,
        category_id: nullable(v.category_id),
        kind: v.kind,
        uom: v.uom || "ea",
        is_serialised: v.is_serialised,
        is_lot_tracked: v.is_lot_tracked,
        is_returnable: v.is_returnable,
        standard_cost: numberOrNull(v.standard_cost),
        costing: v.costing,
        barcode: v.barcode,
        manufacturer_part_no: v.manufacturer_part_no,
        attributes: compactAttributes(defs.data ?? [], attributes),
        tags: v.tags
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean),
        ...(item ? { is_active: v.is_active } : {}),
      };
      const saved = await save.mutateAsync({ id: item?.id, body });
      if (!item) {
        onCreated?.(saved);
      }
    });
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={item ? t("inventory.editItem") : t("inventory.newItem")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
      wide
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="item-name" label={t("app.name")} error={form.errors.name}>
          <Input {...form.field("name")} />
        </Field>
        {!item ? (
          <Field
            id="item-sku"
            label={`${t("inventory.sku")} (${t("app.optional")})`}
            hint={t("assets.codeHint")}
            error={form.errors.sku}
          >
            <Input {...form.field("sku")} />
          </Field>
        ) : null}
        <Field id="item-kind" label={t("app.type")}>
          <Select
            id="item-kind"
            value={form.values.kind}
            onChange={(e) => form.set("kind", e.target.value)}
          >
            {ITEM_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`inventory.kinds.${k}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="item-category_id" label={t("inventory.category")}>
          <CategoryField
            value={form.values.category_id}
            onChange={(id) => form.set("category_id", id)}
            categories={categories.data ?? []}
          />
        </Field>
        <Field id="item-uom" label={t("app.unit")}>
          <Input {...form.field("uom")} />
        </Field>
        <Field
          id="item-standard_cost"
          label={t("inventory.unitCost")}
          error={form.errors.standard_cost}
        >
          <Input type="number" step="any" min="0" {...form.field("standard_cost")} />
        </Field>
        <Field id="item-costing" label={t("inventory.costing")}>
          <Select
            id="item-costing"
            value={form.values.costing}
            onChange={(e) => form.set("costing", e.target.value)}
          >
            {COSTING_METHODS.map((c) => (
              <option key={c} value={c}>
                {t(`inventory.costingMethods.${c}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="item-barcode" label={t("inventory.barcode")}>
          <Input {...form.field("barcode")} />
        </Field>
        <Field id="item-manufacturer_part_no" label={t("inventory.mpn")}>
          <Input {...form.field("manufacturer_part_no")} />
        </Field>
        <Field id="item-tags" label={t("app.tags")} hint={t("app.tagsHint")}>
          <Input {...form.field("tags")} />
        </Field>
      </div>
      <Field id="item-description" label={t("app.description")}>
        <Textarea {...form.field("description")} />
      </Field>
      <div className="flex flex-wrap gap-4">
        <Checkbox
          id="item-is_serialised"
          label={t("inventory.serialised")}
          checked={form.values.is_serialised}
          onChange={(e) => form.set("is_serialised", e.target.checked)}
        />
        <Checkbox
          id="item-is_lot_tracked"
          label={t("inventory.lotTracked")}
          checked={form.values.is_lot_tracked}
          onChange={(e) => form.set("is_lot_tracked", e.target.checked)}
        />
        <Checkbox
          id="item-is_returnable"
          label={t("inventory.returnable")}
          checked={form.values.is_returnable}
          onChange={(e) => form.set("is_returnable", e.target.checked)}
        />
        {item ? (
          <Checkbox
            id="item-is_active"
            label={t("app.active")}
            checked={form.values.is_active}
            onChange={(e) => form.set("is_active", e.target.checked)}
          />
        ) : null}
      </div>
      {defs.data?.length ? (
        <AttributeFields
          definitions={defs.data}
          values={attributes}
          onChange={setAttributes}
          idPrefix="item-attr"
        />
      ) : null}
    </FormDialog>
  );
}

interface PolicyValues extends Record<string, unknown> {
  location_id: string;
  min_qty: string;
  max_qty: string;
  reorder_qty: string;
  lead_time_days: string;
  is_active: boolean;
}

/** Set a min/max reorder policy for an item at a location. */
export function ReorderPolicyDialog({
  item,
  policy,
  onClose,
}: {
  item: Item;
  policy?: ReorderPolicy;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const save = useSetReorderPolicy();
  const initial = useMemo<PolicyValues>(
    () => ({
      location_id: policy?.location_id ?? "",
      min_qty: policy?.min_qty ?? "",
      max_qty: policy?.max_qty ?? "",
      reorder_qty: policy?.reorder_qty ?? "",
      lead_time_days:
        policy?.lead_time_days === null || policy?.lead_time_days === undefined
          ? ""
          : String(policy.lead_time_days),
      is_active: policy?.is_active ?? true,
    }),
    [policy],
  );
  const form = useForm<PolicyValues>({
    initial,
    schema: z.object({
      location_id: z.string().min(1, t("app.required")),
      min_qty: z.coerce.number().min(0, t("app.required")),
    }),
    idPrefix: "policy",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit((v) =>
      save.mutateAsync({
        item_id: item.id,
        location_id: v.location_id,
        min_qty: Number(v.min_qty),
        max_qty: numberOrNull(v.max_qty),
        reorder_qty: numberOrNull(v.reorder_qty),
        lead_time_days: numberOrNull(v.lead_time_days),
        is_active: v.is_active,
      }),
    );
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t("inventory.reorderPolicy")}
      description={`${item.sku} · ${item.name}`}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      <Field
        id="policy-location_id"
        label={t("inventory.location")}
        error={form.errors.location_id}
      >
        <LocationPicker
          id="policy-location_id"
          stockOnly
          value={form.values.location_id}
          onChange={(id) => form.set("location_id", id ?? "")}
          disabled={!!policy}
          required
        />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="policy-min_qty" label={t("inventory.minQty")} error={form.errors.min_qty}>
          <Input type="number" step="any" min="0" {...form.field("min_qty")} />
        </Field>
        <Field id="policy-max_qty" label={t("inventory.maxQty")}>
          <Input type="number" step="any" min="0" {...form.field("max_qty")} />
        </Field>
        <Field
          id="policy-reorder_qty"
          label={t("inventory.reorderQty")}
          hint={t("inventory.reorderQtyHint")}
        >
          <Input type="number" step="any" min="0" {...form.field("reorder_qty")} />
        </Field>
        <Field id="policy-lead_time_days" label={t("inventory.leadTime")}>
          <Input type="number" min="0" {...form.field("lead_time_days")} />
        </Field>
      </div>
      <Checkbox
        id="policy-is_active"
        label={t("app.active")}
        checked={form.values.is_active}
        onChange={(e) => form.set("is_active", e.target.checked)}
      />
    </FormDialog>
  );
}
